import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Drawer,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';

import { EllipsisText, JsonBlock } from '../Assessments/shared';
import { MintApplyResult, MintGate, MintPreviewResult } from './actionTypes';
import {
  ConflictNotice,
  JobOutcomePanel,
  JobStallNotice,
  useJobRun,
} from './JobRun';
import { ScenarioJob } from './types';

/**
 * Mint a scenario draft into a blueprint version: a dry run first, then a
 * confirm that carries the preview's token.
 *
 * Three decisions drive the whole panel.
 *
 * The dry run is the ONLY place the telling is rendered. The apply mints the
 * stored telling verbatim, so "confirm exactly what you previewed" is true
 * even for an uncurated draft, whose model text differs on every render.
 *
 * TWO QUESTIONS, NEVER ONE. `shape` says what the apply DOES to the family —
 * refresh an existing version in place, add a new version, create a new
 * family — and `gate.state` says whether that version still looks as the last
 * mint left it. They were one field, and this screen keyed its danger copy on
 * it, which made it say the opposite of the truth: refreshing a version in
 * place, which replaces the news it serves, its turn schedule and its asset
 * set, rendered green and needed no acknowledgement, while a first-ever mint,
 * which overwrites nothing, was titled "was edited in pm's studio", showed an
 * empty table of differences and demanded a tick. Danger tone, consequence
 * sentence, button label and the mandatory acknowledgement now all key off
 * BOTH answers, and the acknowledgement is asked for where the destruction
 * is: every refresh in place, whatever the fingerprint says.
 *
 * The fingerprint gate is a CONFIRMATION, not an error. A version pm's studio
 * authored, or one minted before provenance was recorded, is the normal state
 * of every family the migration carried across — the dry run succeeds, states
 * the consequence of overwriting it, and waits for the admin to say so. Only
 * those two states make the apply carry `force`, and the preview says which
 * value it will carry, so the confirm token covers it.
 */

type Props = {
  open: boolean;
  draftId: number;
  draftName: string;
  curated: boolean;
  onClose: () => void;
  onApplied?: (result: MintApplyResult, job: ScenarioJob) => void;
};

/** What the apply starts that nobody watches, as `mint.preview` forecasts it. */
type MintChain = {
  will_run?: boolean;
  steps?: string[];
  activates?: boolean;
  notes?: string[];
  why?: string;
};

/** The fields the mint preview adds for this screen. */
type MintPreview = MintPreviewResult & {
  force_required?: boolean;
  chain?: MintChain;
  curation?: {
    rows?: number;
    verdicts?: Record<string, number>;
    workbook?: string;
    imported_at?: string;
  };
};

const REFRESH_IN_PLACE = 'refresh_in_place';

/** "blueprint #58 version 3", with whatever half the gate could name. */
const targetPhrase = (gate: MintGate | undefined, capital = false): string => {
  const blueprint =
    gate?.target_blueprint_id != null
      ? `blueprint #${gate.target_blueprint_id}`
      : 'the blueprint family';
  const version =
    gate?.target_version_number != null
      ? ` version ${gate.target_version_number}`
      : '';
  const phrase = `${blueprint}${version}`;
  return capital ? phrase.charAt(0).toUpperCase() + phrase.slice(1) : phrase;
};

/** The consequence of pressing Apply, in the words that state what it costs. */
const shapeConsequence = (
  result: MintPreview,
  gate: MintGate | undefined,
): string => {
  const target = targetPhrase(gate);
  switch (result.shape) {
    case REFRESH_IN_PLACE:
      return `Applying replaces ${target} in place: the news it serves, its turn schedule and its asset set are overwritten by this telling. Anything edited on that version in pm's studio is lost.`;
    case 'new_version':
      return `Applying adds a new DRAFT version to ${target}. Every version that exists today is left exactly as it is, and nothing trainers are served changes until the new version is published.`;
    case 'new_family':
      return `Applying creates a new blueprint family from this draft, tagged ${
        result.family_tag || 'for this scenario'
      }. Nothing that exists today changes.`;
    default:
      return `Applying writes this telling into ${target}. Review the report above before confirming.`;
  }
};

/** The heading above the consequence: what this apply is, in five words. */
const shapeTitle = (
  result: MintPreview,
  gate: MintGate | undefined,
): string => {
  switch (result.shape) {
    case REFRESH_IN_PLACE:
      return `This replaces ${targetPhrase(gate)} in place`;
    case 'new_version':
      return `This adds a new version to ${targetPhrase(gate)}`;
    case 'new_family':
      return 'This creates a new blueprint family';
    default:
      return 'What this apply will do';
  }
};

/** The label on the button that does it. */
const applyLabel = (
  result: MintPreview,
  gate: MintGate | undefined,
): string => {
  switch (result.shape) {
    case REFRESH_IN_PLACE:
      return gate?.target_version_number != null
        ? `Apply and replace version ${gate.target_version_number}`
        : 'Apply and replace the version';
    case 'new_version':
      return 'Apply and add a new version';
    case 'new_family':
      return 'Apply and create the family';
    default:
      return 'Apply this mint';
  }
};

/** What the admin is ticking, said in the terms of THIS apply. */
const acknowledgementLabel = (gate: MintGate | undefined): string => {
  if (gate?.state === 'edited') {
    return "I have read what changed in pm's studio and want to overwrite it.";
  }
  if (gate?.state === 'no_record') {
    return 'I understand this overwrites a version no recorded mint produced.';
  }
  return `I understand that ${targetPhrase(
    gate,
  )} is replaced, including the news it serves.`;
};

/**
 * The unattended work one Apply starts. A mint that creates a version asks for
 * its own benchmark, and the chain runs a score, a push and a gate verdict
 * with no further click — which the consequence sentence never mentioned.
 */
const ChainNotice: React.FC<{ chain: MintChain | undefined }> = ({ chain }) => {
  if (!chain?.will_run) return null;
  const steps = chain.steps || [];
  return (
    <div>
      <Typography.Paragraph>
        Applying also starts work nobody watches: the new version asks for its
        own answer key
        {steps.length
          ? `, and ${steps.join(', then ')} run`
          : ' and the work runs'}{' '}
        with no further click. They land a DRAFT answer key
        {chain.activates === false
          ? ' — activating one is always a separate, human decision.'
          : '.'}
      </Typography.Paragraph>
      {(chain.notes || []).map(note => (
        <Typography.Paragraph key={note}>
          <Typography.Text type="secondary">{note}</Typography.Text>
        </Typography.Paragraph>
      ))}
    </div>
  );
};

/** The fingerprint verdict on the version a refresh overwrites. */
const FingerprintNotice: React.FC<{ gate: MintGate | undefined }> = ({
  gate,
}) => {
  if (!gate) return null;
  const target = targetPhrase(gate, true);
  const explanation =
    gate.state === 'no_record'
      ? `${target} was authored in pm's studio, or minted before provenance was recorded, so there is nothing to compare this telling against. Applying overwrites whatever it holds.`
      : gate.state === 'edited'
        ? `${target} no longer matches the fingerprint the last mint recorded, so somebody has edited it in pm's studio since. Those edits are overwritten.`
        : `${target} still matches the fingerprint the last recorded mint wrote, so nobody has edited it in pm's studio. That is not the same as safe: this apply replaces what the version serves either way.`;

  const diff = gate.edit_diff;
  const diffRows = Array.isArray(diff)
    ? (diff as Array<Record<string, unknown>>)
    : null;
  const unavailable =
    !diffRows &&
    diff != null &&
    typeof diff === 'object' &&
    (diff as { available?: boolean }).available === false
      ? String((diff as { why?: string }).why || '')
      : '';
  const rawDiff =
    !diffRows &&
    !unavailable &&
    diff != null &&
    typeof diff === 'object' &&
    Object.keys(diff as object).length > 0
      ? diff
      : null;

  return (
    <div>
      <Typography.Paragraph>{explanation}</Typography.Paragraph>
      {gate.fingerprint_recorded && gate.fingerprint_live && (
        <Typography.Paragraph>
          <Typography.Text type="secondary">
            Edit fingerprint {String(gate.fingerprint_recorded).slice(0, 12)} on
            record, {String(gate.fingerprint_live).slice(0, 12)} live.
          </Typography.Text>
        </Typography.Paragraph>
      )}
      {diffRows && diffRows.length > 0 && (
        <Table
          rowKey={(_, index) => String(index)}
          className="assessment-table"
          size="small"
          tableLayout="fixed"
          dataSource={diffRows}
          pagination={false}
          columns={[
            {
              title: 'What differs',
              dataIndex: 'path',
              width: 260,
              render: (value: unknown) => (
                <EllipsisText code value={String(value ?? '—')} />
              ),
            },
            {
              title: 'On record from the last mint',
              dataIndex: 'recorded',
              render: (value: unknown) => (
                <EllipsisText value={JSON.stringify(value ?? null)} />
              ),
            },
            {
              title: 'Live in pm now',
              dataIndex: 'live',
              render: (value: unknown) => (
                <EllipsisText value={JSON.stringify(value ?? null)} />
              ),
            },
          ]}
        />
      )}
      {unavailable && (
        <Typography.Paragraph>
          <Typography.Text type="secondary">
            No list of differences: {unavailable}.
          </Typography.Text>
        </Typography.Paragraph>
      )}
      {rawDiff != null && <JsonBlock compact value={rawDiff} />}
    </div>
  );
};

/**
 * One notice for the whole decision: what the apply does, what the fingerprint
 * says about the version it overwrites, what it starts afterwards, and — only
 * when something is destroyed — the acknowledgement, beside the sentence that
 * describes the destruction.
 */
const ConsequenceNotice: React.FC<{
  result: MintPreview;
  gate: MintGate | undefined;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
}> = ({ result, gate, acknowledged, onAcknowledge }) => {
  const destructive = result.shape === REFRESH_IN_PLACE;
  return (
    <Alert
      className="assessment-callout"
      type={destructive ? 'warning' : 'info'}
      showIcon
      title={shapeTitle(result, gate)}
      description={
        <div>
          <Typography.Paragraph>
            {shapeConsequence(result, gate)}
          </Typography.Paragraph>
          {destructive && <FingerprintNotice gate={gate} />}
          <ChainNotice chain={result.chain} />
          {destructive && (
            <Checkbox
              checked={acknowledged}
              onChange={event => onAcknowledge(event.target.checked)}
            >
              {acknowledgementLabel(gate)}
            </Checkbox>
          )}
        </div>
      }
    />
  );
};

const countTag = (label: string, value: unknown) => (
  <Tag key={label}>
    {label} {typeof value === 'number' ? value : 0}
  </Tag>
);

const PreviewReport: React.FC<{ result: MintPreview }> = ({ result }) => {
  const unmapped = result.unmapped_assets || [];
  const warnings = result.warnings || [];
  // A curated telling reports its verdicts under `curation`, a model-rendered
  // one at the top level. Reading only the second meant this row never
  // rendered for a curated draft, which is every draft on the fleet.
  const verdicts = result.verdicts || result.curation?.verdicts || {};
  return (
    <div className="assessment-detail-stack">
      <Card size="small" title="What this telling ships">
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Copy">
            <Tag color={result.curated ? 'green' : 'blue'}>
              {result.curated ? 'CURATED' : `MODEL ${result.model || '?'}`}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Family tag">
            <EllipsisText code value={result.family_tag} />
          </Descriptions.Item>
          <Descriptions.Item label="Telling size">
            {result.telling_bytes != null
              ? `${(Number(result.telling_bytes) / 1024).toFixed(1)} KiB`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="News rows" span={2}>
            <Space size={[4, 4]} wrap className="assessment-tag-group">
              {countTag('items', result.items)}
              {countTag('synthetic', result.synthetic)}
              {countTag('filler', result.filler)}
              {countTag('edited', result.edited)}
              {result.weekend_shifts
                ? countTag('weekend shifts', result.weekend_shifts)
                : null}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Filler source">
            {/* A curated telling states no filler source at all; saying
                "POOLS" for it would be the screen inventing a fact. */}
            {!result.filler_source ? (
              '—'
            ) : result.filler_source === 'none' ? (
              <Tag color="orange">NONE</Tag>
            ) : (
              <Tag>{result.filler_source.toUpperCase()}</Tag>
            )}
          </Descriptions.Item>
          {Object.keys(verdicts).length > 0 && (
            <Descriptions.Item label="Curation verdicts" span={3}>
              <Space size={[4, 4]} wrap className="assessment-tag-group">
                {Object.entries(verdicts).map(([verdict, count]) => (
                  <Tag key={verdict}>
                    {verdict.toUpperCase()} {count}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Content digest" span={3}>
            <EllipsisText code value={result.content_digest} />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Assets this telling resolves">
        {unmapped.length ? (
          <Alert
            className="assessment-callout"
            type="warning"
            showIcon
            title={`${unmapped.length} asset${
              unmapped.length === 1 ? '' : 's'
            } in this telling resolve to no PM instrument`}
            description={
              <Space size={[4, 4]} wrap className="assessment-tag-group">
                {unmapped.map(code => (
                  <Tag color="red" key={code}>
                    {code}
                  </Tag>
                ))}
              </Space>
            }
          />
        ) : (
          <Alert
            className="assessment-callout"
            type="success"
            showIcon
            title="Every asset in this telling resolves to a PM instrument"
          />
        )}
        {result.asset_resolution && (
          <JsonBlock compact value={result.asset_resolution} />
        )}
      </Card>

      {warnings.length > 0 && (
        <Card size="small" title={`Warnings (${warnings.length})`}>
          {warnings.map((warning, index) => (
            <Typography.Paragraph key={index}>{warning}</Typography.Paragraph>
          ))}
        </Card>
      )}
    </div>
  );
};

const ApplyReport: React.FC<{ result: MintApplyResult }> = ({ result }) => (
  <div className="assessment-detail-stack">
    <Descriptions bordered size="small" column={3}>
      <Descriptions.Item label="Blueprint">
        {result.blueprint_id != null ? `#${result.blueprint_id}` : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Version">
        {result.version != null ? `v${result.version}` : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Template session">
        {result.template_session_id != null
          ? `#${result.template_session_id}`
          : '—'}
      </Descriptions.Item>
      <Descriptions.Item label="Serving tag" span={2}>
        <EllipsisText code value={result.serving_tag} />
      </Descriptions.Item>
      <Descriptions.Item label="Result">
        <Tag color={result.created_family ? 'blue' : 'purple'}>
          {result.created_family
            ? 'NEW FAMILY'
            : result.refreshed_in_place
              ? 'REFRESHED IN PLACE'
              : 'NEW VERSION'}
        </Tag>
      </Descriptions.Item>
      {result.news && (
        <Descriptions.Item label="News the import wrote" span={3}>
          <Space size={[4, 4]} wrap className="assessment-tag-group">
            {Object.entries(result.news).map(([key, value]) => (
              <Tag key={key}>
                {key.replace(/_/g, ' ')} {String(value)}
              </Tag>
            ))}
          </Space>
        </Descriptions.Item>
      )}
    </Descriptions>
    {result.pm_edits_overwritten && (
      <Alert
        className="assessment-callout"
        type="warning"
        showIcon
        title="Edits made in pm's studio were overwritten"
        description="This mint was applied past the fingerprint gate; the record on the draft says so."
      />
    )}
    {result.missing_assets && result.missing_assets.length > 0 && (
      <Alert
        className="assessment-callout"
        type="warning"
        showIcon
        title={`${result.missing_assets.length} asset(s) the import could not bind`}
        description={result.missing_assets.join(', ')}
      />
    )}
  </div>
);

const MintPanel: React.FC<Props> = ({
  open,
  draftId,
  draftName,
  curated,
  onClose,
  onApplied,
}) => {
  const preview = useJobRun();
  const apply = useJobRun();
  const [retell, setRetell] = useState(false);
  const [model, setModel] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) {
      preview.reset();
      apply.reset();
      setAcknowledged(false);
    }
    // The panel is a one-shot workflow: closing it abandons the run rather
    // than leaving a half-confirmed preview on screen for the next open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const previewResult = (
    preview.job?.status === 'SUCCEEDED' ? preview.job.result : null
  ) as MintPreview | null;
  const applyResult = (
    apply.job?.status === 'SUCCEEDED' ? apply.job.result : null
  ) as MintApplyResult | null;
  const gate = previewResult?.gate;
  const applied = Boolean(applyResult);

  // `force` on an APPLY means one thing: override the fingerprint gate. The
  // preview says whether this apply needs it, and the confirm token covers
  // that value — so sending it when the gate did not ask breaks the token
  // rather than quietly overwriting more than the admin approved.
  const forceRequired =
    typeof previewResult?.force_required === 'boolean'
      ? previewResult.force_required
      : gate?.state === 'edited' || gate?.state === 'no_record';
  // The tick is asked for where the destruction is: an in-place refresh
  // replaces that version whatever the fingerprint says.
  const needsAcknowledgement = previewResult?.shape === REFRESH_IN_PLACE;

  const runPreview = async () => {
    setAcknowledged(false);
    apply.reset();
    await preview.run('mint.preview', {
      draft_id: draftId,
      force: retell,
      model: model || '',
    });
  };

  const runApply = async () => {
    const previewJob = preview.job;
    if (!previewJob) return;
    const finished = await apply.run('mint.apply', {
      draft_id: draftId,
      preview_job: previewJob.id,
      confirm_token: previewJob.confirm_token || '',
      force: forceRequired,
      acknowledged_refusal: forceRequired ? 'blueprint_edited_since_mint' : '',
    });
    if (finished?.status === 'SUCCEEDED') {
      onApplied?.((finished.result || {}) as MintApplyResult, finished);
    }
  };

  const consequence = previewResult
    ? [
        shapeConsequence(previewResult, gate),
        previewResult.chain?.will_run
          ? 'It also starts a score, an answer key push and a gate verdict that run unattended.'
          : '',
      ]
        .filter(Boolean)
        .join(' ')
    : '';
  const applyDisabled =
    !previewResult ||
    apply.running ||
    applied ||
    (needsAcknowledgement && !acknowledged);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={960}
      title={`Mint draft #${draftId} — ${draftName}`}
      destroyOnHidden
    >
      <div className="assessment-detail-stack">
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="The dry run writes nothing"
          description="It renders the telling, resolves its assets and reads the target blueprint's edit fingerprint. Nothing is written until you apply, and the apply mints exactly the telling shown here — it never re-renders."
        />

        <Card size="small" title="Dry run options">
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {curated && (
              <Checkbox
                checked={retell}
                onChange={event => setRetell(event.target.checked)}
              >
                Re-tell with the model instead of the curated sheet. The
                curators' reviewed headlines are ignored for this run.
              </Checkbox>
            )}
            <Typography.Text type="secondary">
              Starved periods are not filled from the corpus pools here: the
              pools are harvested by the corpus-lane mint_scenario command,
              which reads the newsgraph database directly. This dry run shows
              the telling the curated rows produce.
            </Typography.Text>
            {(!curated || retell) && (
              <Input
                aria-label="Model"
                placeholder="Model (blank uses the configured default)"
                style={{ maxWidth: 420 }}
                value={model}
                onChange={event => setModel(event.target.value)}
              />
            )}
            <Space>
              <Button
                type="primary"
                loading={preview.running}
                onClick={runPreview}
              >
                Run dry run
              </Button>
              {preview.job && !preview.running && (
                <Typography.Text type="secondary">
                  Preview job {preview.job.id}
                </Typography.Text>
              )}
            </Space>
          </Space>
        </Card>

        <ConflictNotice job={preview.conflict || apply.conflict} />
        {(preview.error || apply.error) && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={preview.error || apply.error}
          />
        )}

        <JobOutcomePanel
          job={preview.job}
          successTitle="Dry run complete"
          elapsedMs={preview.elapsedMs}
        />
        <JobStallNotice stall={preview.stall} />
        {previewResult && (
          <>
            <PreviewReport result={previewResult} />
            <ConsequenceNotice
              result={previewResult}
              gate={gate}
              acknowledged={acknowledged}
              onAcknowledge={setAcknowledged}
            />
            {!applied && (
              <Card size="small" title="Apply this mint">
                <Popconfirm
                  title="Apply this mint?"
                  description={consequence}
                  okText={
                    needsAcknowledgement
                      ? 'Replace the version'
                      : 'Apply the mint'
                  }
                  cancelText="Not yet"
                  disabled={applyDisabled}
                  onConfirm={runApply}
                >
                  <Button
                    type="primary"
                    danger={needsAcknowledgement}
                    disabled={applyDisabled}
                    loading={apply.running}
                  >
                    {applyLabel(previewResult, gate)}
                  </Button>
                </Popconfirm>
              </Card>
            )}
          </>
        )}

        <JobOutcomePanel
          job={apply.job}
          successTitle="The mint was applied"
          elapsedMs={apply.elapsedMs}
        />
        <JobStallNotice stall={apply.stall} />
        {applyResult && <ApplyReport result={applyResult} />}
      </div>
    </Drawer>
  );
};

export default MintPanel;
